/**
 * Closing Package Frontend models — aligned with Backend projection / composition APIs
 * (IMP-001F / closingPackageProjectionBuilder + composition services).
 *
 * Source of Truth: Backend response contracts. FE does not invent workflow states.
 */

/** Legacy Closing Workflow phase (still returned by projection). */
export type ClosingPackagePhase =
  | 'NOT_AVAILABLE'
  | 'COLLECTING'
  | 'READY_TO_SEND'
  | 'DISPATCHED'
  | 'RECEIVED_BY_CLIENT';

/** Authoritative 13-state workflow (workflowState). */
export type ClosingPackageWorkflowState =
  | 'NOT_AVAILABLE'
  | 'DRAFT'
  | 'FORMED'
  | 'SCAN_REVIEW_REQUIRED'
  | 'SCANS_SENT_TO_CLIENT'
  | 'WAITING_CLIENT_APPROVAL'
  | 'REVISION_REQUIRED'
  | 'CLIENT_APPROVED'
  | 'ORIGINALS_PREPARATION'
  | 'ORIGINALS_SENT'
  | 'CLIENT_RECEIVED'
  | 'COMPLETED'
  | 'CANCELLED';

export type ClosingPackageItemState =
  | 'MISSING'
  | 'UPLOADED'
  | 'SENT'
  | 'RECEIVED'
  | 'CONFIRMED';

export type CompositionMode = 'RECOMMENDED' | 'DRAFT' | 'FORMED';

export type CompositionStatus = 'INCLUDED' | 'EXCLUDED';

export type ClosingPackageApprovalResult = 'APPROVED' | 'REJECTED';

export type ClosingPackageTaskSummary = {
  id: number;
  entityType: string;
  entityId: number;
  taskType: string;
  status: string;
  dueDate: string | null;
  routeId: number | null;
  applicationId: number | null;
  groupId: number | null;
  routeStopId: number | null;
  invoiceId: number | null;
  obligationId: number | null;
};

export type ClosingPackageDispatchSummary = {
  id: number;
  dispatchType: string;
  dispatchedAt: string;
  documentIds: number[];
  dispatchedBy: number;
};

/** Document link as returned inside projection items (registry-row shaped). */
export type ClosingPackageDocumentLink = {
  id: number;
  documentType: string;
  fileName: string;
  typeLabel: string;
  customDocumentName?: string | null;
  sourceLabel: string;
  catalogCode?: string | null;
  legacyType?: string | null;
  ownerScope?: string | null;
  route: { id: number; label: string | null; carrierId?: number | null; carrierName?: string | null } | null;
  application: {
    id: number;
    internalNumber: string;
    externalNumber?: string;
    clientName?: string | null;
  } | null;
  createdAt: string;
  fileType?: string | null;
  stage?: string | null;
  comment?: string | null;
  category?: string;
};

export type CompositionItemProjectionMeta = {
  compositionItemId: number;
  itemKey: string;
  routeId: number | null;
  compositionStatus: CompositionStatus;
  isManual: boolean;
  sortOrder: number;
  linkedDocumentId: number | null;
};

export type ClosingPackageItem = {
  key: string;
  label: string;
  party: 'CLIENT' | 'CARRIER';
  catalogCode: string;
  legacyTypes: string[];
  ownerScope: string | null;
  required: boolean;
  stage: 'closing' | 'settlement';
  financeImpact?: string[];
  taskImpact?: string[];
  state: ClosingPackageItemState;
  documents: ClosingPackageDocumentLink[];
  openTasks: ClosingPackageTaskSummary[];
  relatedTasks: ClosingPackageTaskSummary[];
  dispatches: ClosingPackageDispatchSummary[];
  compositionMeta?: CompositionItemProjectionMeta | null;
};

export type ClosingPackageWorkflowMetadata = {
  approvalCycle: number;
  lastApprovalResult: ClosingPackageApprovalResult | null;
  lastApprovalAt: string | null;
  lastApprovalBy: number | null;
  /** Optional — populated when Backend projection exposes approval comment. */
  lastApprovalComment?: string | null;
  preparedAt: string | null;
  preparedBy: number | null;
  originalsDispatchedAt: string | null;
  originalsDispatchedBy: number | null;
  originalsEstimatedReceiptAt: string | null;
  originalsReceivedAt: string | null;
  locked: boolean;
};

export type ClosingPackageProjection = {
  applicationId: number;
  phase: ClosingPackagePhase;
  availability: {
    allRoutesCompleted: boolean;
    hasCompletedRoute: boolean;
    routeCount: number;
    contractType: string | null;
    applicationStatus: string | null;
  };
  items: ClosingPackageItem[];
  blockers: ClosingPackageTaskSummary[];
  dispatches: ClosingPackageDispatchSummary[];
  generatedAt: string;
  formed: boolean;
  formedAt: string | null;
  formedBy: { id: number; username: string } | null;
  locked: boolean;
  compositionMode: CompositionMode;
  compositionItemCount: number;
  workflowState: ClosingPackageWorkflowState;
  workflowStateReason: string;
  workflowMetadata: ClosingPackageWorkflowMetadata;
  closingPackageOriginalsCycle: number;
  originalsDispatchCycleHistory: ClosingPackageOriginalsDispatchCycleDto[];
};

/** GET /applications/:id/closing-package/composition */
export type ClosingPackageCompositionItemDto = {
  id: number;
  itemKey: string;
  routeId: number | null;
  documentId: number | null;
  itemStatus: CompositionStatus;
  isManual: boolean;
  sortOrder: number;
  document?: ClosingPackageDocumentLink | null;
  route?: {
    id: number;
    loadingCity?: string | null;
    unloadingCity?: string | null;
  } | null;
  updatedAt: string;
};

export type ClosingPackageCompositionResult = {
  applicationId: number;
  locked: boolean;
  formed: boolean;
  formedAt: string | null;
  formedBy: { id: number; username: string } | null;
  items: ClosingPackageCompositionItemDto[];
};

/** GET /applications/:id/closing-package/composition/journal */
export type ClosingPackageJournalEntry = {
  id: number;
  action: string;
  compositionItemId: number | null;
  itemKey: string | null;
  documentId: number | null;
  previousDocumentId: number | null;
  routeId: number | null;
  oldValue: unknown;
  newValue: unknown;
  userId: number | null;
  userName: string | null;
  comment: string | null;
  createdAt: string;
};

export type ClosingPackageJournalResult = {
  applicationId: number;
  entries: ClosingPackageJournalEntry[];
};

/** POST /applications/:id/closing-package/composition/initialize */
export type ClosingPackageInitializeCompositionResult = {
  created: boolean;
  compositionMode: 'DRAFT';
  items: ClosingPackageCompositionItemDto[];
};

export type ClosingPackageConfirmFormationResult = {
  applicationId: number;
  formed: boolean;
  formedAt: string;
  formedBy: { id: number; username: string };
  itemCount: number;
  compositionMode: 'FORMED';
};

/** Mutation request bodies (match backend Zod DTOs). */
export type AddClosingPackageItemBody = {
  documentId: number;
  itemKey?: string;
  routeId?: number | null;
  sortOrder?: number;
  comment?: string | null;
};

export type ReplaceClosingPackageItemBody = {
  documentId: number;
  comment?: string | null;
};

export type ClosingPackageCommentBody = {
  comment?: string | null;
  formedAt?: string | null;
};

export type ClosingPackageScanDispatchMethod = 'EMAIL' | 'EDO' | 'MESSENGER' | 'PORTAL' | 'OTHER';

export type RegisterScanDispatchBody = {
  dispatchMethod: ClosingPackageScanDispatchMethod;
  recipient?: string;
  comment?: string | null;
  dispatchedAt?: string;
};

export type RegisterClientApprovalBody = {
  result: ClosingPackageApprovalResult;
  source: 'PHONE' | 'EMAIL' | 'EDO' | 'MEETING' | 'OTHER';
  comment?: string | null;
  dispatchId?: number;
  registeredAt?: string;
};

export type RegisterOriginalsPreparedBody = {
  comment?: string | null;
  preparedAt?: string;
};

export type OriginalsDispatchMethod = 'MAIL' | 'COURIER' | 'CARRIER' | 'EDO' | 'HAND' | 'OTHER';

export type RegisterOriginalsDispatchBody = {
  dispatchMethod: OriginalsDispatchMethod;
  recipient: string;
  carrierName?: string | null;
  trackingNumber?: string | null;
  estimatedReceiptAt?: string;
  comment?: string | null;
  previousDispatchId?: number | null;
  dispatchedAt?: string;
};

export type ConfirmClientReceiptBody = {
  result?: 'RECEIVED' | 'PROBLEM';
  confirmationSource: 'PHONE' | 'EMAIL' | 'EDO' | 'CLIENT_MESSAGE' | 'SIGNED_RECEIPT' | 'OTHER';
  receivedAt?: string;
  problemType?:
    | 'MISSING_DOCUMENTS'
    | 'WRONG_DOCUMENTS'
    | 'DAMAGED_PACKAGE'
    | 'COURIER_ISSUE'
    | 'WRONG_RECIPIENT'
    | 'CLIENT_REJECTED'
    | 'OTHER';
  comment?: string | null;
};

export type RegisterEdoDispatchBody = {
  recipient: string;
  comment?: string | null;
  dispatchedAt?: string;
};

export type ConfirmEdoReceiptBody = {
  result?: 'RECEIVED' | 'RESEND' | 'REVISION';
  receivedAt?: string;
  comment?: string | null;
};

export type ClosingPackageOriginalsDispatchCycleDto = {
  cycleNumber: number;
  preparedAt: string | null;
  dispatchedAt: string | null;
  estimatedReceiptAt: string | null;
  receivedAt: string | null;
  deliveryMethod: string | null;
  trackingNumber: string | null;
  confirmationResult: 'RECEIVED' | 'PROBLEM';
  problemType: ConfirmClientReceiptBody['problemType'] | null;
  comment: string | null;
  sealedAt: string;
};

/** Финансовые документы перевозчика не входят в комплект (Business Standard). */
export const CARRIER_FINANCIAL_PACKAGE_KEYS = new Set([
  'CARRIER_INVOICE',
  'CARRIER_UPD',
  'CARRIER_TN_TTH_ORIGINALS',
]);

export function filterClientClosingPackageItems(items: ClosingPackageItem[]): ClosingPackageItem[] {
  return items.filter(
    (item) => item.party === 'CLIENT' && !CARRIER_FINANCIAL_PACKAGE_KEYS.has(item.key)
  );
}
